/**
 * @fileoverview Header, list, and footer for the B2B events page.
 * @module pages/b2bEvents/B2BEventsSections
 */

import LanguageSelector from "../../components/LanguageSelector/index.jsx";
import NotificationBell from "../../components/NotificationBell/index.jsx";
import SearchBar from "../../components/SearchBar/index.jsx";
import ResultsDropdown from "../../components/ResultsDropdown";
import PageSelector from "../../components/PageSelector";
import { ReactComponent as TitleIcon } from "../../assets/icons/BusinessEvents/TitleIcon.svg";
import { ReactComponent as AddIcon } from "../../assets/icons/BusinessEvents/AddIcon.svg";
import { ReactComponent as CheckCircleIcon } from "../../assets/icons/BusinessEvents/CheckCircleIcon.svg";
import styles from "../../styles/B2BEvents.module.css";
import { b2bEventListKey } from "./b2bEventsLayout";
import B2BEventCard from "./B2BEventCard";

/**
 * @param {Object} props
 * @returns {JSX.Element}
 */
export const B2BEventsHeader = ({ page }) => (
  <div className={styles.header}>
    <div className={styles.languages}>
      <NotificationBell navigate={page.navigate} />
      <LanguageSelector />
    </div>
    <div className={styles.headerInfoContainer}>
      <div className={styles.headerInfo}>
        <TitleIcon className={styles.titleIcon} />
        <h1 className={styles.title}>{page.t("pages.b2bEvents.header")}</h1>
      </div>
      <div className={styles.searchSection}>
        <SearchBar
          placeholder={
            page.viewport.isSmallMobile || page.viewport.isMediumMobile
              ? page.t("pages.b2bEvents.mobileSearchText")
              : page.t("pages.b2bEvents.searchText")
          }
          value={page.searchQuery}
          onChange={page.handleSearch}
          width={page.searchBarWidth}
        />
        {page.canManageStaff && (
          <div className={styles.newRequirementButton} onClick={page.openCreateForm}>
            <AddIcon />
            {!page.viewport.isSmallMobile && !page.viewport.isMediumMobile ? (
              <span>{page.t("pages.b2bEvents.createEvent")}</span>
            ) : (
              ""
            )}
          </div>
        )}
      </div>
    </div>
  </div>
);

/**
 * @param {Object} props
 * @returns {JSX.Element}
 */
export const B2BEventsList = ({ page }) => (
  <div className={styles.eventsList}>
    {page.paginatedEventsList.length === 0 && (
      <div className={styles.noResults}>
        <p>{page.t("pages.b2bEvents.noResults")}</p>
      </div>
    )}
    {page.paginatedEventsList.length > 0 &&
      page.paginatedEventsList.map((event) => (
        <B2BEventCard
          key={b2bEventListKey(event)}
          event={event}
          t={page.t}
          user={page.user}
          navigate={page.navigate}
          canManageStaff={page.canManageStaff}
          showVirtualLineCompaniesButton={page.showVirtualLineCompaniesButton}
          onOpenSchedules={page.openBuyerSchedulesModal}
        />
      ))}
  </div>
);

/**
 * @param {Object} props
 * @returns {JSX.Element}
 */
export const B2BEventsFooter = ({ page }) => (
  <div className={styles.footer}>
    <ResultsDropdown onResultsLimitChange={page.handleResultsLimitChange} />
    <PageSelector
      currentPage={page.currentPage}
      totalPages={page.totalPages}
      onPageChange={page.handlePageChange}
    />
  </div>
);

/**
 * @param {Object} props
 * @returns {JSX.Element|null}
 */
export const B2BEventsSuccess = ({ page }) => {
  if (!page.showSuccessMessage) {
    return null;
  }
  return (
    <div
      className={`${styles.successMessage} ${page.isSlidingUp ? styles.slideUp : ""}`}
    >
      <CheckCircleIcon className={styles.successMessageIcon} />
      {page.t("pages.b2bEvents.creationMessage")}
    </div>
  );
};
